/**
 * Below-the-app SEO content — H1, How it works, Use cases, Why local OCR,
 * FAQ. Translated copy lives in `MarketingContent.i18n.ts` keyed by
 * UiLangCode; this file is the layout / icon mapping.
 *
 * The English copy is the source of truth and is mirrored verbatim by
 * the FAQPage and HowTo JSON-LD blocks inside `index.html`. Phase 4
 * (per-language URLs) ships per-language `index.html` shells so the
 * structured data matches the rendered language too — until then,
 * non-English visitors render translated body text but Google sees
 * the English schema. That's still net-positive for SEO: rich
 * results in English-search-context, translated content otherwise.
 */

import { Database, FileText, Globe2, Lightbulb, Lock, Mail, Receipt as ReceiptIcon, Wallet, Workflow } from 'lucide-react';

import type { UiLangCode } from '../lib/i18n';
import { CollapsibleSection } from './CollapsibleSection';
import { MARKETING_BY_LANG, type MarketingStrings } from './MarketingContent.i18n';

const USE_CASE_ICONS: Record<MarketingStrings['useCases'][number]['key'], React.ComponentType<{ className?: string }>> = {
  tax: Wallet,
  expense: FileText,
  freelance: ReceiptIcon,
  split: Globe2,
};

interface Props {
  lang: UiLangCode;
}

export function MarketingContent({ lang }: Props): JSX.Element {
  // noUncheckedIndexedAccess turns Record<UiLangCode, ...> lookups into
  // `T | undefined` even though the Record is exhaustive over the union.
  // Fall back to English with a runtime guarantee so the component is
  // still well-typed.
  const m: MarketingStrings = (MARKETING_BY_LANG[lang] ??
    MARKETING_BY_LANG.eng) as MarketingStrings;

  return (
    <section
      aria-label="About Receipt Ripper"
      className="space-y-10 border-t border-slate-200 pt-8 sm:space-y-16 sm:pt-12 dark:border-slate-800"
    >
      {/* Hero (H1 + subtitle) lives at the top of the page now, rendered
          by <MarketingHero /> above the ad slot. Removed here so we
          don't ship two H1s. */}

      {/* How it works */}
      <div className="space-y-4 sm:space-y-6">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900 sm:text-2xl dark:text-slate-50">
          <Workflow className="h-5 w-5 text-indigo-600 sm:h-6 sm:w-6 dark:text-indigo-400" aria-hidden="true" />
          {m.howItWorksHeading}
        </h2>
        <ol className="grid gap-3 sm:grid-cols-3 sm:gap-6">
          {m.steps.map((step) => (
            <li
              key={step.title}
              className="rounded-lg border border-slate-200 bg-white p-3 sm:p-5 dark:border-slate-800 dark:bg-slate-900"
            >
              <h3 className="mb-1 text-sm font-semibold text-slate-900 sm:mb-2 sm:text-base dark:text-slate-50">{step.title}</h3>
              <p className="text-xs text-slate-600 sm:text-sm dark:text-slate-400">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>

      {/* Why local OCR */}
      <div className="space-y-3 sm:space-y-4">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900 sm:text-2xl dark:text-slate-50">
          <Lock className="h-5 w-5 text-indigo-600 sm:h-6 sm:w-6 dark:text-indigo-400" aria-hidden="true" />
          {m.whyLocalHeading}
        </h2>
        <div className="space-y-2 text-sm text-slate-600 sm:space-y-3 sm:text-base dark:text-slate-400">
          {m.whyLocalParagraphs.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      </div>

      {/* Use cases — expanded by default. Past iteration collapsed these
          for compactness; AdSense quality review reads content depth off
          the rendered DOM, not the toggle-state, so we keep them open. */}
      <CollapsibleSection
        defaultOpen
        trigger={
          <h2 className="inline-flex items-center gap-2 align-middle text-lg font-bold text-slate-900 sm:text-2xl dark:text-slate-50">
            <Lightbulb className="h-5 w-5 text-indigo-600 sm:h-6 sm:w-6 dark:text-indigo-400" aria-hidden="true" />
            {m.useCasesHeading}
          </h2>
        }
      >
        <div className="mt-3 grid gap-3 sm:mt-6 sm:grid-cols-2 sm:gap-6">
          {m.useCases.map((u) => {
            const Icon = USE_CASE_ICONS[u.key];
            return (
              <div
                key={u.key}
                className="rounded-lg border border-slate-200 bg-white p-3 sm:p-5 dark:border-slate-800 dark:bg-slate-900"
              >
                <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-900 sm:mb-2 sm:text-base dark:text-slate-50">
                  <Icon className="h-4 w-4 text-indigo-600 sm:h-5 sm:w-5 dark:text-indigo-400" aria-hidden="true" />
                  {u.title}
                </h3>
                <p className="text-xs text-slate-600 sm:text-sm dark:text-slate-400">{u.body}</p>
              </div>
            );
          })}
        </div>
      </CollapsibleSection>

      {/* FAQ moved to its own page (/faq) — the landing-page section
          would otherwise duplicate the same content. A small link below
          tells visitors where it went. */}
      <div className="space-y-3 sm:space-y-4">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900 sm:text-2xl dark:text-slate-50">
          <Database className="h-5 w-5 text-indigo-600 sm:h-6 sm:w-6 dark:text-indigo-400" aria-hidden="true" />
          {m.faqHeading}
        </h2>
        <p className="text-sm text-slate-600 sm:text-base dark:text-slate-400">
          {(() => {
            const parts = m.faqCta.body.split('{link}');
            return (
              <>
                {parts[0]}
                <a
                  href={m.faqCta.href}
                  className="font-medium text-indigo-600 underline-offset-4 hover:underline dark:text-indigo-400"
                >
                  {m.faqCta.linkLabel}
                </a>
                {parts[1]}
              </>
            );
          })()}
        </p>
      </div>

      {/* Feedback CTA — visible (not collapsed) so it's actually noticed.
          Body string contains the literal `{email}` token; split on it
          so the address renders as a real mailto link rather than plain
          text. Single placeholder per string keeps the parsing trivial. */}
      <div className="space-y-3 sm:space-y-4">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900 sm:text-2xl dark:text-slate-50">
          <Mail className="h-5 w-5 text-indigo-600 sm:h-6 sm:w-6 dark:text-indigo-400" aria-hidden="true" />
          {m.feedback.heading}
        </h2>
        <p className="text-sm text-slate-600 sm:text-base dark:text-slate-400">
          {(() => {
            const parts = m.feedback.body.split('{email}');
            return (
              <>
                {parts[0]}
                <a
                  href="mailto:contact@receipt-ripper.com"
                  className="font-medium text-indigo-600 underline-offset-4 hover:underline dark:text-indigo-400"
                >
                  contact@receipt-ripper.com
                </a>
                {parts[1]}
              </>
            );
          })()}
        </p>
      </div>
    </section>
  );
}
